import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import ModalShell from '../../Attendance/Component/ModalShell';
import { deleteShift, updateShift } from '../../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';

// Deleting a shift only succeeds while nothing references it. The usual answer
// is 409 SHIFT_IN_USE with the counts, which is not really a failure — it is
// the moment to offer deactivation instead, which stops the shift being
// assigned while leaving the history readable.
const DeleteShiftModal = ({ shift, onClose, onDone }) => {
    const dispatch = useDispatch();
    const { shiftMutationLoading } = useSelector(state => state.ShiftReducer || {});
    // null until the delete comes back refused: { assignments }
    const [inUse, setInUse] = useState(null);

    useEffect(() => { setInUse(null); }, [shift?.shift_id]);

    if (!shift) return null;

    const handleDelete = () => {
        dispatch(deleteShift({
            shift_id: shift.shift_id,
            onSuccess: () => { onClose(); onDone?.(); },
            onError: (info) => {
                if (info.code === 'SHIFT_IN_USE') {
                    setInUse({ assignments: info.data?.assignments ?? 0 });
                }
            },
        }));
    };

    const handleDeactivate = () => {
        dispatch(updateShift({
            shift_id: shift.shift_id,
            is_active: false,
            onSuccess: () => { onClose(); onDone?.(); },
        }));
    };

    return (
        <ModalShell
            isOpen={Boolean(shift)}
            title={inUse ? 'This shift is in use' : `Delete ${shift.shift_name}?`}
            icon={inUse ? 'mdi:alert-circle-outline' : 'mdi:trash-can-outline'}
            onClose={onClose}
            busy={shiftMutationLoading}
            actions={
                inUse ? (
                    <button
                        type="button"
                        onClick={handleDeactivate}
                        disabled={shiftMutationLoading}
                        className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-amber-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-amber-700 disabled:tw-opacity-50"
                    >
                        {shiftMutationLoading ? 'Saving…' : 'Deactivate instead'}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleDelete}
                        disabled={shiftMutationLoading}
                        className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-red-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-red-700 disabled:tw-opacity-50"
                    >
                        {shiftMutationLoading ? 'Deleting…' : 'Delete shift'}
                    </button>
                )
            }
        >
            {inUse ? (
                <div className="tw-text-sm tw-text-gray-600">
                    <p className="tw-mb-2">
                        <strong>{shift.shift_name}</strong> is assigned to{' '}
                        <strong>{inUse.assignments}</strong> employee{inUse.assignments === 1 ? '' : 's'}, so it cannot be deleted.
                    </p>
                    <p className="tw-mb-0 tw-text-xs tw-text-gray-500">
                        Deactivating it keeps all of that history readable and simply removes the shift from the assignment picker.
                    </p>
                </div>
            ) : (
                <div className="tw-text-sm tw-text-gray-600">
                    <p className="tw-mb-2">
                        This permanently removes the <strong>{shift.shift_code}</strong> shift definition.
                    </p>
                    <p className="tw-mb-0 tw-text-xs tw-text-gray-500 tw-flex tw-items-start tw-gap-1.5">
                        <Icon icon="mdi:information-outline" className="tw-mt-0.5 tw-flex-shrink-0" />
                        If anything still references it you will be offered deactivation instead.
                    </p>
                </div>
            )}
        </ModalShell>
    );
};

export default DeleteShiftModal;
